import Image from 'next/image';

import { Box, Flex, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import { CheckGlyph } from '~/features/government/components/check-glyph';
import {
  societalImpactContent,
  societalImpactCustomSolutionBullets,
  SocietalImpactPillar,
  societalImpactPillars,
  SocietalImpactPillarTheme,
  SocietalImpactProgramCard,
  societalImpactProgramCards,
} from '~/features/government/configs/societal-impact-content';

const STRIPES_SRC = '/images/government/societal-impact/stripes.svg';
const EYEBROW_ICON_SRC = '/images/government/societal-impact/eyebrow-heart.svg';

// Blue-200 / Purple-200 are not in the v2 theme; reuse the established literals already used by
// neighboring `government-*` sections (see success-stories-content.ts).
type PillarTokens = {
  cardBg: string;
  cardBorder: string;
  divider: string;
};

const PILLAR_TOKENS: Record<SocietalImpactPillarTheme, PillarTokens> = {
  blue: {
    cardBg: 'blue.50',
    cardBorder: 'blue.100',
    divider: '#AFC6FF',
  },
  purple: {
    cardBg: 'purple.50',
    cardBorder: 'purple.100',
    divider: '#EBBAFF',
  },
};

// Vertical fade for the program-card left separator. Figma node 159:24933 renders a flat
// Purple-500 border, but per design review we fade it top→bottom for a softer divider.
const PROGRAM_CARD_DIVIDER_GRADIENT = 'linear-gradient(180deg, #931CF5 0%, rgba(147, 28, 245, 0) 100%)';

export type GovernmentSocietalImpactProps = {
  pretitle?: string;
  heading?: string;
  bottomHeading?: string;
  bottomIntro?: string;
  customSolutionsTitle?: string;
  pillars?: SocietalImpactPillar[];
  programCards?: SocietalImpactProgramCard[];
  customSolutionBullets?: string[];
};

export function GovernmentSocietalImpact({
  pretitle = societalImpactContent.pretitle,
  heading = societalImpactContent.heading,
  bottomHeading = societalImpactContent.bottomHeading,
  bottomIntro = societalImpactContent.bottomIntro,
  customSolutionsTitle = societalImpactContent.customSolutionsTitle,
  pillars = societalImpactPillars,
  programCards = societalImpactProgramCards,
  customSolutionBullets = societalImpactCustomSolutionBullets,
}: GovernmentSocietalImpactProps) {
  return (
    <Flex
      as='section'
      bg='white'
      flexDirection='column'
      gap={{ base: '40px', md: '0' }}
      pb={{ base: '0', md: '104px' }}
      pt={{ base: '48px', md: '104px' }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          direction='column'
          gap={{ base: '40px', md: '72px' }}
          margin='0 auto'
          maxW={{ base: '100%', md: '1056px' }}
        >
          <SectionHeader
            color='grey.500'
            containerProps={{ alignItems: 'center', margin: '0 auto', maxW: { base: '306px', md: '880px' } }}
            subtitle={pretitle}
            iconSlot={
              <Box h={{ base: '14px', md: '18px' }} position='relative' w={{ base: '14px', md: '18px' }}>
                <Image alt='' src={EYEBROW_ICON_SRC} style={{ objectFit: 'contain' }} fill />
              </Box>
            }
            titleSlot={
              <Heading
                as='h2'
                color='grey.900'
                fontFamily='heading'
                fontSize={{ base: '2.375rem', md: '3.375rem' }}
                fontWeight='600'
                letterSpacing='-0.02em'
                lineHeight='1.1'
                maxW={{ base: '100%', md: '810px' }}
              >
                {heading}
              </Heading>
            }
          />
          <PillarRow pillars={pillars} />
        </Flex>
      </ContentContainer>

      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          direction='column'
          gap={{ base: '40px', md: '56px' }}
          margin='0 auto'
          maxW={{ base: '100%', md: '1056px' }}
        >
          <BottomTitle bottomHeading={bottomHeading} bottomIntro={bottomIntro} />
          <ProgramCardGrid cards={programCards} />
        </Flex>
      </ContentContainer>

      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Box
          maxW={{ base: 'none', md: '1056px' }}
          mt={{ base: '0', md: '56px' }}
          mx={{ base: '-16px', md: 'auto' }}
          width={{ base: 'calc(100% + 32px)', md: '100%' }}
        >
          <CustomSolutionsPanel bullets={customSolutionBullets} title={customSolutionsTitle} />
        </Box>
      </ContentContainer>
    </Flex>
  );
}

function PillarRow({ pillars }: { pillars: SocietalImpactPillar[] }) {
  return (
    <Flex
      borderBottom='1px solid'
      borderColor='grey.200'
      direction={{ base: 'column', md: 'row' }}
      gap={{ base: '12px', md: '56px' }}
      margin='0 auto'
      maxW={{ base: '358px', md: '880px' }}
      pb={{ base: '48px', md: '72px' }}
      w='100%'
    >
      {pillars.map((pillar) => (
        <PillarCard key={pillar.id} pillar={pillar} />
      ))}
    </Flex>
  );
}

function PillarCard({ pillar }: { pillar: SocietalImpactPillar }) {
  const tokens = PILLAR_TOKENS[pillar.theme];

  return (
    <Stack
      bg={tokens.cardBg}
      border='1px solid'
      borderColor={tokens.cardBorder}
      borderRadius='8px'
      flex={{ md: '1 0 0' }}
      p={{ base: '20px', md: '32px' }}
      spacing={{ base: '16px', md: '24px' }}
      w={{ base: '100%', md: 'auto' }}
    >
      <Flex
        align={{ base: 'center', md: 'flex-start' }}
        borderBottom='1px solid'
        borderColor={tokens.divider}
        direction={{ base: 'row', md: 'column' }}
        gap={{ base: '16px', md: '16px' }}
        pb={{ base: '16px', md: '24px' }}
        w='100%'
      >
        <Box flexShrink={0} h={{ base: '40px', md: '48px' }} position='relative' w={{ base: '40px', md: '48px' }}>
          <Image alt={pillar.pictogramAlt} src={pillar.pictogramSrc} style={{ objectFit: 'contain' }} fill />
        </Box>
        <Heading
          as='h3'
          color='grey.900'
          flex={{ base: '1', md: 'none' }}
          fontFamily='heading'
          fontSize={{ base: '1.125rem', md: '1.5rem' }}
          fontWeight='600'
          letterSpacing='-0.01em'
          lineHeight={{ base: '1.2', md: '1.15' }}
        >
          {pillar.heading}
        </Heading>
      </Flex>
      <Text
        color='grey.700'
        fontSize={{ base: '0.875rem', md: '1rem' }}
        letterSpacing={{ base: '-0.01em', md: 'normal' }}
        lineHeight={{ base: '1.4', md: '1.5' }}
      >
        {pillar.body}
      </Text>
    </Stack>
  );
}

function BottomTitle({ bottomHeading, bottomIntro }: { bottomHeading: string; bottomIntro: string }) {
  return (
    <Stack
      alignItems='center'
      margin='0 auto'
      maxW={{ base: '100%', md: '880px' }}
      pt={{ base: '0', md: '56px' }}
      spacing={{ base: '16px', md: '24px' }}
      textAlign='center'
    >
      <Heading
        as='h3'
        color='grey.900'
        fontFamily='heading'
        fontSize={{ base: '1.75rem', md: '2.25rem' }}
        fontWeight='600'
        letterSpacing='-0.01em'
        lineHeight={{ base: '1.15', md: '1.1' }}
      >
        {bottomHeading}
      </Heading>
      <Text
        color='grey.500'
        fontSize={{ base: '0.875rem', md: '1rem' }}
        letterSpacing={{ base: '-0.01em', md: 'normal' }}
        lineHeight={{ base: '1.4', md: '1.5' }}
        maxW={{ base: '100%', md: '700px' }}
      >
        {bottomIntro}
      </Text>
    </Stack>
  );
}

function ProgramCardGrid({ cards }: { cards: SocietalImpactProgramCard[] }) {
  return (
    <SimpleGrid
      columns={{ base: 1, md: 2 }}
      gap={{ base: '8px', md: '16px' }}
      margin='0 auto'
      maxW={{ base: '100%', md: '1056px' }}
      w='100%'
    >
      {cards.map((card) => (
        <ProgramCard key={card.id} card={card} />
      ))}
    </SimpleGrid>
  );
}

function ProgramCard({ card }: { card: SocietalImpactProgramCard }) {
  const { Icon } = card;
  return (
    <Flex
      align='flex-start'
      bg='grey.50'
      border='1px solid'
      borderColor='grey.100'
      borderRadius='4px'
      gap={{ base: '16px', md: '24px' }}
      px={{ base: '16px', md: '24px' }}
      py={{ base: '14px', md: '24px' }}
    >
      <Flex
        align='center'
        flexShrink={0}
        h={{ base: '18px', md: '24px' }}
        justify='center'
        w={{ base: '18px', md: '24px' }}
      >
        <Icon boxSize={{ base: '18px', md: '24px' }} color='purple.500' />
      </Flex>
      <Stack
        flex='1'
        minW='0'
        pl={{ base: '16px', md: '24px' }}
        position='relative'
        spacing='6px'
        _before={{
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '2px',
          background: PROGRAM_CARD_DIVIDER_GRADIENT,
        }}
      >
        <Heading
          as='h4'
          color='grey.900'
          fontFamily='heading'
          fontSize={{ base: '1rem', md: '1.125rem' }}
          fontWeight='600'
          letterSpacing={{ base: 'normal', md: '-0.01em' }}
          lineHeight='1.1'
        >
          {card.title}
        </Heading>
        <Text color='grey.700' fontSize={{ base: '0.75rem', md: '0.8125rem' }} lineHeight='1.4'>
          {card.body}
        </Text>
      </Stack>
    </Flex>
  );
}

function CustomSolutionsPanel({ bullets, title }: { bullets: string[]; title: string }) {
  return (
    <Box
      borderRadius={{ base: '0', md: '8px' }}
      overflow='hidden'
      position='relative'
      px={{ base: '16px', md: '32px' }}
      py={{ base: '40px', md: '32px' }}
      w='100%'
      backgroundImage={{
        base: 'linear-gradient(134.87deg, #00298C 0%, #031643 100%)',
        md: 'linear-gradient(108.76deg, #00298C 0%, #031643 53.47%)',
      }}
    >
      <Stack align={{ base: 'center', md: 'stretch' }} position='relative' spacing='24px' zIndex={1}>
        <Box borderBottom='1px solid' borderColor='blue.700' pb='24px' w='100%'>
          <Text
            color='white'
            fontFamily='heading'
            fontSize={{ base: '1.375rem', md: '1.5rem' }}
            fontWeight='600'
            letterSpacing={{ base: 'normal', md: '-0.01em' }}
            lineHeight={{ base: '1.2', md: '1.15' }}
            textAlign={{ base: 'center', md: 'left' }}
          >
            {title}
          </Text>
        </Box>

        <SimpleGrid
          columnGap={{ base: '0', md: '16px' }}
          columns={{ base: 1, md: 2 }}
          px={{ base: '16px', md: '0' }}
          rowGap={{ base: '12px', md: '16px' }}
          w='100%'
        >
          {bullets.map((bullet) => (
            <CustomSolutionBullet key={bullet} text={bullet} />
          ))}
        </SimpleGrid>
      </Stack>

      <Box
        bottom='0'
        display={{ base: 'none', md: 'block' }}
        h='219px'
        mixBlendMode='overlay'
        pointerEvents='none'
        position='absolute'
        right='0'
        w='219px'
        zIndex={0}
      >
        <Image alt='' src={STRIPES_SRC} style={{ objectFit: 'contain', objectPosition: 'bottom right' }} fill />
      </Box>
    </Box>
  );
}

function CustomSolutionBullet({ text }: { text: string }) {
  return (
    <Flex align='flex-start' gap={{ base: '8px', md: '12px' }} w='100%'>
      <Flex
        align='center'
        color='#D2DFFF'
        flexShrink={0}
        h={{ base: '20px', md: '24px' }}
        justify='center'
        w={{ base: '14px', md: '18px' }}
      >
        <CheckGlyph />
      </Flex>
      <Text
        color='#D2DFFF'
        fontSize={{ base: '0.875rem', md: '1rem' }}
        letterSpacing={{ base: '-0.01em', md: 'normal' }}
        lineHeight={{ base: '1.4', md: '1.5' }}
      >
        {text}
      </Text>
    </Flex>
  );
}
